import { inject, Injectable } from '@angular/core';
import { catchError, EMPTY, finalize, from, Observable, tap, throwError } from 'rxjs';

import { ApiOptions } from '@shared/models';
import { CustomToastrService, SpinnerService, PocketbaseService } from '@shared/services';
import { UserRequest } from '@case-editor/models';

@Injectable({
  providedIn: 'root'
})
export class UserRequestsApiService {
  private readonly pocketbaseService = inject(PocketbaseService);
  private readonly spinnerService = inject(SpinnerService);
  private readonly customToastrService = inject(CustomToastrService);

  updateUserRequest(id: string, userRequest: Partial<UserRequest>, options?: ApiOptions) {
    const pb = this.pocketbaseService.getPocketBase();

    if (options?.showSpinner) {
      this.spinnerService.startSpinner();
    }

    return from(pb.collection('requests').update<UserRequest>(id, userRequest))
      .pipe(
        tap(() =>
          options?.showSuccessMessage && this.customToastrService.showSuccess(options?.successMessage ?? 'Erfolgreich aktualisiert')
        ),
        catchError((error) => {
          if (!options?.withoutErrorMessage) {
            this.customToastrService.showError(options?.errorMessage ?? (error?.message || 'Es ist ein Fehler aufgetreten. Bitte versuche es später noch einmal.'))
          }

          return throwError(() => new Error(error));
        }),
        finalize(() => options?.showSpinner && this.spinnerService.stopSpinner())
      );
  }

  getRequest(id: string, options?: ApiOptions): Observable<UserRequest> {
    return from(this.pocketbaseService.getPocketBase().collection('requests').getOne<UserRequest>(id, {
      expand: 'case'
    }))
      .pipe(
        tap(() =>
          options?.showSuccessMessage && this.customToastrService.showSuccess(options?.successMessage ?? 'Erfolgreich aktualisiert')
        ),
        catchError((error) => {
          if (!options?.withoutErrorMessage) {
            this.customToastrService.showError(options?.errorMessage ?? (error?.message || 'Es ist ein Fehler aufgetreten. Bitte versuche es später noch einmal.'));
          }

          return EMPTY;
        }),
        finalize(() => options?.showSpinner && this.spinnerService.stopSpinner())
      )
  }

  updateEditRoute(id: string, route: string) {
    return from(this.pocketbaseService.getPocketBase().collection('requests').update(id, {
      'edit_route': route
    }));
  }
}
